import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../../contexts/DataContext';
import { useToast } from '../../contexts/ToastContext';
import SidebarItemMenu from './SidebarItemMenu';
import ProcessingSection from './ProcessingSection';
import { useGenerationJobs } from '../../hooks/useGenerationJobs';
import './SidebarItemMenu.css';
import './FlashcardHistoryPanel.css';

const FC_PIN_KEY = 'flashcardHistory.pinnedIds';

const FlashcardHistoryPanel = ({ 
  currentDeckId, 
  onSelectDeck, 
  onNewDeck,
  showNewButton = true,
  hideHeader = false,
  hideSearch = false,
  showSectionHeader = false,
  onToggleCollapse,
  sidebarCollapsed,
  externalSearchQuery
}) => {
  const { getFlashcardDecks, clearCache, updateFlashcardDeck, deleteFlashcardDeck } = useData();
  const { addToast } = useToast();
  const { processingJobs, failedJobs, dismissJob: dismissGenJob } = useGenerationJobs('flashcards');
  
  const [deckHistory, setDeckHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [pinnedIds, setPinnedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FC_PIN_KEY)) || []; } catch { return []; }
  });

  const togglePin = useCallback((id) => {
    setPinnedIds(prev => {
      const next = prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id];
      try { localStorage.setItem(FC_PIN_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const handleRename = useCallback((id, currentTitle) => {
    setRenaming(id);
    setRenameValue(currentTitle || '');
  }, []);

  const handleRenameSubmit = useCallback(async (id) => {
    const trimmed = renameValue.trim();
    if (!trimmed) { setRenaming(null); return; }
    try {
      await updateFlashcardDeck(id, { title: trimmed });
      setDeckHistory(prev => prev.map(d => (d.id === id || d.deck_id === id) ? { ...d, title: trimmed } : d));
      addToast({ intent: 'success', message: 'Deck renamed' });
    } catch (err) {
      console.error('Failed to rename deck:', err);
      addToast({ intent: 'danger', message: 'Failed to rename deck' });
    }
    setRenaming(null);
    setRenameValue('');
  }, [renameValue, updateFlashcardDeck, addToast]);

  const handleDeleteDeck = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this deck?')) return;
    try {
      await deleteFlashcardDeck(id);
      setDeckHistory(prev => prev.filter(d => d.id !== id && d.deck_id !== id));
      clearCache('flashcard-decks');
      addToast({ intent: 'success', message: 'Deck deleted' });
    } catch (err) {
      console.error('Failed to delete deck:', err);
      addToast({ intent: 'danger', message: 'Failed to delete deck' });
    }
  }, [deleteFlashcardDeck, clearCache, addToast]);

  // Listen for flashcard creation events to refresh the list
  useEffect(() => {
    const handleFlashcardsCreated = () => {
      clearCache('flashcard-decks');
      setRefreshCounter(c => c + 1);
    };
    window.addEventListener('flashcards:created', handleFlashcardsCreated);
    return () => window.removeEventListener('flashcards:created', handleFlashcardsCreated);
  }, [clearCache]);

  // Load decks from DataContext
  useEffect(() => {
    const loadDecks = async () => {
      try {
        setLoading(true);
        setError(null);
        const decks = await getFlashcardDecks();
        
        // Transform to match expected format
        const transformedDecks = (decks || []).map(deck => {
          // Backend list endpoint doesn't include cards/count - would need individual fetch
          // Using documentIds length as rough indicator for now
          const cardCount = deck.documentIds?.length ? '~' + (deck.documentIds.length * 10) : '?';
          return {
            deck_id: deck.id,
            title: deck.title,
            num_cards: cardCount,
            generatedAt: deck.createdAt,
            docCount: deck.documentIds?.length || 0,
            ...deck
          };
        });
        
        // Sort by creation date (newest first)
        transformedDecks.sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt));
        
        setDeckHistory(transformedDecks);
      } catch (err) {
        console.error('Failed to load flashcard decks:', err);
        setError('Failed to load decks');
      } finally {
        setLoading(false);
      }
    };

    loadDecks();
  }, [getFlashcardDecks, refreshCounter]);



  const effectiveSearchQuery = externalSearchQuery != null ? externalSearchQuery : searchQuery;
  const filteredDecks = deckHistory.filter(deck => 
    deck.title?.toLowerCase().includes(effectiveSearchQuery.toLowerCase())
  ).sort((a, b) => {
    const aid = a.id || a.deck_id;
    const bid = b.id || b.deck_id;
    const ap = pinnedIds.includes(aid) ? 0 : 1;
    const bp = pinnedIds.includes(bid) ? 0 : 1;
    return ap - bp;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now - date;
    if (diffMs < 0) return 'just now';
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };


  return (
    <div className="flashcard-history-panel">
      {!hideHeader && (
        <div className="flashcard-history-panel__header">
          <div className="flashcard-history-panel__header-row">
            {onToggleCollapse && (
              <button
                type="button"
                className="flashcard-history-panel__collapse-btn"
                onClick={onToggleCollapse}
                title="Collapse sidebar"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M9 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 4l-3 4 3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            
            {!hideSearch && (
              <div className="flashcard-history-panel__search">
                <input
                  type="text"
                  placeholder="Search decks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flashcard-history-panel__search-input"
                />
              </div>
            )}
            
            {onNewDeck && (
              <button 
                className="flashcard-history-panel__circular-new-btn"
                onClick={onNewDeck}
                title="Create New Deck"
              >
                +
              </button>
            )}
          </div>
        </div>
      )}

      {/* Section Header - sticky within scroll area */}
      {showSectionHeader && (
        <div className="flashcard-history-section-header">
          <span className="flashcard-history-section-title">Flashcard History</span>
          <span className="flashcard-history-count-pill">{filteredDecks.length}</span>
        </div>
      )}

      <div className="flashcard-history-panel__list">
        <ProcessingSection
          processingJobs={processingJobs}
          failedJobs={failedJobs}
          onDismiss={dismissGenJob}
        />
        {loading ? (
          <div className="flashcard-history-panel__empty">
            <p>Loading decks...</p>
          </div>
        ) : error ? (
          <div className="flashcard-history-panel__empty">
            <p className="flashcard-history-panel__error">{error}</p>
          </div>
        ) : filteredDecks.length === 0 && deckHistory.length === 0 && processingJobs.length === 0 && failedJobs.length === 0 ? (
          <div className="flashcard-history-panel__empty">
            <div className="flashcard-history-panel__tutorial">
              <div className="flashcard-history-panel__tutorial-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 8h20"/></svg>
              </div>
              <h4 className="flashcard-history-panel__tutorial-title">
                Build Your First Deck
              </h4>
              <p className="flashcard-history-panel__tutorial-text">
                Turn your course docs into interactive flashcards built for review, recall, and exam prep.
              </p>
              <p className="flashcard-history-panel__tutorial-hint">Great for memorization-heavy classes</p>
            </div>
          </div>
        ) : filteredDecks.length === 0 ? (
          <div className="flashcard-history-panel__empty">
            <p>No decks match your search.</p>
          </div>
        ) : (
          filteredDecks.map((deck) => {
            const deckId = deck.id || deck.deck_id;
            const isPinned = pinnedIds.includes(deckId);
            return (
            <div
              key={deck.deck_id}
              className={`fh-card ${String(currentDeckId) === String(deck.deck_id) ? 'is-active' : ''} ${isPinned ? 'is-pinned' : ''}`}
              onClick={() => { if (renaming !== deckId) onSelectDeck && onSelectDeck(deck); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectDeck && onSelectDeck(deck); } }}
            >
              {/* Title */}
              <div className="fh-top">
                {isPinned && (
                  <span className="fh-pin-icon">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><path d="M12 17v5"/><path d="M9 2h6l-1 7h4l-5 8h-2l-5-8h4Z"/></svg>
                  </span>
                )}
                <div className="fh-title" title={deck.title || 'Untitled Flashcard Set'}>
                  {renaming === deckId ? (
                    <input
                      className="sim-rename-input"
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRenameSubmit(deckId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRenameSubmit(deckId);
                        else if (e.key === 'Escape') { setRenaming(null); setRenameValue(''); }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    deck.title || <span className="fh-title-empty">Untitled Flashcard Set</span>
                  )}
                </div>
                <SidebarItemMenu
                  onRename={() => handleRename(deckId, deck.title)}
                  onPin={() => togglePin(deckId)}
                  isPinned={isPinned}
                  onDelete={() => handleDeleteDeck(deckId)}
                />
              </div>

              {/* Metadata chips */}
              <div className="fh-meta">
                <span className="fh-chip">📅 {formatDate(deck.generatedAt)}</span>
                {deck.docCount > 0 && (
                  <span className="fh-chip">📄 {deck.docCount} doc{deck.docCount !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default FlashcardHistoryPanel;
